import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import {
  TransactionMultiStepWorkflowstatus,
  TransactionStatus
} from '~/enums/transactions/transaction-status.enum';
import type { SnackbarMessage, TransactionHistoryStep } from '~/types';
import { useTransactionsStore } from '~/stores/transactions-store';
import { useStore } from '@/stores/store';
import useTransactionsApi from '~/composables/api/useTransactionsApi';

export const useCheckRequest = (emit?: (event: 'transactionValidated') => void) => {
  const {
    checkRequestForm,
    checkRequestModal,
    transactionInfos,
    checkRequestBody,
    bankAccountInfos,
    history
  } = storeToRefs(useTransactionsStore());
  const { getTransactionHistory } = useTransactionsApi();
  const { t, n, locale } = useI18n();
  const { add } = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();
  const { getDisplayExpr } = useUtils();
  const { checkRequest } = useTransactionsApi();
  const { loadArray } = storeToRefs(useStore());
  const currency = ref(localStorage.getItem('partnerCurrency') || 'EUR');

  const transactionBalance = computed(() =>
    transactionInfos.value
      ? n(transactionInfos.value.amount, { style: 'currency', currency: currency.value })
      : undefined
  );

  const isSubmitDisabled = computed(() => {
    return (
      loadArray.value.includes('completeCheckRequest') ||
      loadArray.value.includes('cancelCheckRequest') ||
      !checkRequestForm.value.password.value ||
      isAllValidationsDone.value ||
      userAlreadyValidated.value
    );
  });

  const checkRequestApprouveArgs = computed(() => ({
    text: t('transactions.details.actions.checkRequestApprouve'),
    isDisabled: isSubmitDisabled.value,
    iconPosition: 'left',
    leftIcon: 'Check',
    isLoading: loadArray.value.includes('completeCheckRequest')
  }));

  const checkRequestDeclineArgs = computed(() => ({
    text: t('transactions.details.actions.checkRequestRefuse'),
    isDisabled: isSubmitDisabled.value,
    iconPosition: 'left',
    leftIcon: 'Close',
    isLoading: loadArray.value.includes('cancelCheckRequest')
  }));

  const isAllValidationsDone = computed(() => {
    const moneyOutValidationConfig = getMoneyOutValidationConfig();
    const numberOfValidators = moneyOutValidationConfig.moneyOut.numberOfValidators;
    const numberOfValidatorsNumber = parseInt(numberOfValidators);

    const numberOfValidatorsAlreadyDone = history.value.filter((step) => step.ActionResult).length;

    return numberOfValidatorsAlreadyDone >= numberOfValidatorsNumber;
  });

  const userAlreadyValidated = computed(() => {
    const userId = Number(localStorage.getItem('userId'));
    return history.value.some(
      (step) =>
        step.UserId === userId &&
        step?.ActionResult &&
        step.ActionResult === TransactionMultiStepWorkflowstatus.Approved
    );
  });

  const checkRequestAlertArgs = computed(() => ({
    type: 'no-title',
    theme: 'danger',
    text: t(
      isAllValidationsDone.value
        ? 'transactions.details.transactionHistory.validationsDoneDescription'
        : 'transactions.details.actions.checkRequestDisabled'
    )
  }));

  const showAlert = computed(() => isAllValidationsDone.value || userAlreadyValidated.value);

  const modalArgs = computed(() => {
    const moneyOutValidationConfig = getMoneyOutValidationConfig();
    if (transactionInfos.value?.id && moneyOutValidationConfig) {
      const numberOfValidators = moneyOutValidationConfig.moneyOut.numberOfValidators;
      const numberOfValidatorsNumber = parseInt(numberOfValidators);

      const numberOfValidatorsAlreadyDone = history.value.filter(
        (step) => step.ActionResult
      ).length;

      const validationCounter =
        numberOfValidatorsAlreadyDone < numberOfValidatorsNumber
          ? numberOfValidatorsAlreadyDone + 1 + '/' + numberOfValidatorsNumber
          : numberOfValidatorsAlreadyDone + '/' + numberOfValidatorsNumber;

      return {
        title: t('transactions.details.actions.checkRequest', {
          validationCounter
        }),
        icon: 'RefuseValidate',
        cancelButtonText: t('common.cancel')
      };
    } else {
      return {
        title: t('transactions.details.actions.checkRequest'),
        icon: 'RefuseValidate',
        cancelButtonText: t('common.cancel')
      };
    }
  });

  const senderChipArgs = computed(() => {
    const {
      walletIsCompany: isCompany,
      walletIntId: id,
      walletFirstName: firstName,
      walletLastName: lastName,
      walletCompanyName: companyName
    } = transactionInfos.value ?? {};

    const baseChipArgs = {
      dataQa: 'check-request-sender-chip'
    };

    if (id === 4) {
      return {
        ...baseChipArgs,
        text: 'Lemonway',
        icon: 'Lemonway',
        theme: 'success'
      };
    }

    if (id === 11) {
      return {
        ...baseChipArgs,
        text: t('navigation.myWallet'),
        icon: 'Storefront',
        theme: 'info'
      };
    }

    return {
      ...baseChipArgs,
      text: isCompany ? companyName : `${firstName} ${lastName}`,
      icon: isCompany ? 'MyAccount' : 'Person',
      theme: 'neutral'
    };
  });

  const receiverChipArgs = computed(() => ({
    text: bankAccountInfos.value?.RIB_Owner,
    icon: 'ContentBox',
    theme: 'neutral',
    dataQa: 'check-request-receiver-chip'
  }));

  const receiverEntityArgs = computed(() => ({
    hasIcon: 'true',
    icon: 'ContentBox',
    title: t('transactions.details.actions.checkRequestEntityTitle', {
      country: getDisplayExpr('alpha2', bankAccountInfos.value?.codeCountry)
    }),
    subtitles: [bankAccountInfos.value?.iban_ciban, bankAccountInfos.value?.RIB_Owner],
    theme: 'neutral',
    isClickable: false,
    highlighted: true
  }));

  const outgoingReceiverEntityArgs = computed(() => ({
    hasIcon: true,
    icon: 'ContentBox',
    title: bankAccountInfos.value?.RIB_Owner,
    subtitles: [
      t('transactions.details.actions.checkRequestEntityTitle', {
        country: getDisplayExpr('alpha2', bankAccountInfos.value?.codeCountry)
      }),
      bankAccountInfos.value?.iban_ciban || t('transactions.unknownIBAN')
    ],
    theme: 'neutral',
    isClickable: false,
    highlighted: true
  }));

  const commentLabel = computed(() => `${t('common.comment')} ${t('common.optionalInput')}`);

  const toggleCheckRequestModal = async () => {
    checkRequestModal.value = !checkRequestModal.value;

    if (transactionInfos.value?.id) {
      await loadTransactionHistory(transactionInfos.value?.id);
    }

    if (!checkRequestModal.value) {
      checkRequestForm.value.password = { value: '', isValid: false };
      checkRequestForm.value.comment = { value: '', isValid: false };
    }
  };

  const submitCheckRequest = async (status: TransactionStatus) => {
    try {
      const userId = Number(localStorage.getItem('userId'));
      if (userId && transactionInfos.value?.walletExtId) {
        const loadingFlag =
          status === TransactionStatus.Completed ? 'completeCheckRequest' : 'cancelCheckRequest';
        checkRequestBody.value = {
          transactionId: transactionInfos.value.id,
          userId,
          Language: locale.value,
          Password: checkRequestForm.value.password.value,
          Status: status,
          Comment: checkRequestForm.value.comment.value || null,
          Wallet: transactionInfos.value.walletExtId
        };
        await checkRequest(checkRequestBody.value, loadingFlag);
        add({
          type: 'success',
          message: t(
            `transactions.details.actions.${status === TransactionStatus.Completed ? 'successComplete' : 'successCancel'}`,
            {
              transactionId: transactionInfos.value.id
            }
          )
        } as SnackbarMessage);
        toggleCheckRequestModal();
        emit?.('transactionValidated');
      }
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const checkMoneyOutValidationConfig = () => {
    const validationList = localStorage.getItem('validationConfiguration');
    return (
      validationList !== null &&
      validationList !== 'null' &&
      Object.keys(JSON.parse(validationList)).includes('moneyOut')
    );
  };

  const getMoneyOutValidationConfig = () => {
    const validationList = localStorage.getItem('validationConfiguration');
    return validationList !== null ? JSON.parse(validationList) : {};
  };

  const loadTransactionHistory = async (id: number) => {
    const data = await getTransactionHistory(String(id));
    history.value = data as TransactionHistoryStep[];
  };

  return {
    modalArgs,
    checkRequestApprouveArgs,
    checkRequestDeclineArgs,
    transactionBalance,
    senderChipArgs,
    receiverChipArgs,
    receiverEntityArgs,
    outgoingReceiverEntityArgs,
    commentLabel,
    checkRequestAlertArgs,
    showAlert,
    submitCheckRequest,
    toggleCheckRequestModal,
    checkMoneyOutValidationConfig,
    getMoneyOutValidationConfig
  };
};
